import React, { useRef } from "react";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { colors, CustomSectionTitle } from "@ippon/shared-components";
import { AREA_TYPE_LABELS, type AreaType } from "@ippon/shared-types";
import { BackButton } from "@/common/BackButton";

interface TabConfigItem {
  id: string;
  label: string;
}

interface DetailPageHeaderProps {
  /** PreviewDrawer 等に埋め込まれている場合 true (sticky top: 0 にする) */
  embedded?: boolean;
  /** タイトル（部屋名 / 区画名）。空ならタイトル領域は空表示 */
  title: string;
  /** 物件名（タイトル直下に小さく表示） */
  propertyName: string;
  /** 附帯収益区画なら true (現在は未使用、将来チップ等で利用予定) */
  isAncillary: boolean;
  /** 場所区分 (area_type) — チップ表示に利用 */
  areaType?: AreaType;
  /** タブ定義 */
  tabs: ReadonlyArray<TabConfigItem>;
  /** 現在のタブ index */
  activeTabIndex: number;
  /** タブ切替コールバック */
  onTabChange: (nextIndex: number) => void;
}

/**
 * 区画詳細 ページのスティッキー・ヘッダ。
 *
 * - タイトル + 場所区分バッジ（専有部 / 共用部 / 建物設備 / 敷地 / その他）+ 物件名（小）
 * - 編集アイコン
 * - 親タブ（区画詳細 / テナント管理 / 工事履歴 / 契約履歴）
 */
const DetailPageHeader: React.FC<DetailPageHeaderProps> = ({
  embedded = false,
  title,
  propertyName,
  isAncillary: _isAncillary,
  areaType,
  tabs,
  activeTabIndex,
  onTabChange,
}) => {
  const areaTypeLabel = areaType ? AREA_TYPE_LABELS[areaType] : undefined;
  // Stack の内側幅 (= タイトル + chip が並ぶスペース) を CustomSectionTitle 側の
  // truncate 計算に渡すための ref
  const titleStackRef = useRef<HTMLDivElement>(null);
  return (
    <Box
      sx={{
        // embedded (PreviewDrawer 等) では親側がスクロールするため sticky で
        // 上端に貼り付ける。通常表示の部屋詳細ページではページ全体が固定高さ +
        // overflow:hidden になっており、ヘッダは静的に top に置かれる。
        ...(embedded
          ? { position: "sticky", top: 0 }
          : { position: "relative" }),
        zIndex: 10,
        backgroundColor: "white",
        // 通常ページは親の負マージン分だけ広げ背景を両端まで敷く。
        // 埋め込み(プレビュードロワー)時は親(PreviewDrawer)の padding が異なり、
        // 負マージンだと右にはみ出して左右スクロール＆本文とのズレが出るので 0 に。
        mx: embedded ? 0 : { xs: -1, md: -2 },
        px: embedded ? 0 : { xs: 1, md: 2 },
        flexShrink: 0,
      }}
    >
      {/* 物件詳細の PropertyDetailHeader（物件名）の pl と揃えるため、
          ここでは追加の ml を付けない（旧: ml でグリッド相殺していた分が左ズレの原因） */}
      {/* 戻るボタン表示時(単独ページ)は行の左をタブ/コンテンツ左端に揃える(ml:0)。 */}
      <Box sx={{ mb: 2.2, ml: !embedded ? 0 : 1.5 }}>
        <Stack
          ref={titleStackRef}
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <CustomSectionTitle
            // title が空の間 (= データ取得中) は何も出さず、専有部/附帯収益等の
            // フォールバック文字列を一瞬表示しない。
            title={title || ""}
            addBorder={false}
            size="large"
            // 単独ページ(物件詳細から遷移)時のみ「戻る」ボタンを icon スロットで表示。
            // icon を渡すと青い縦バー(borderLeft)が消え、戻るボタンが左に並ぶ。
            icon={!embedded ? <BackButton size={24} /> : undefined}
            // chip をタイトル直後に並べたい (= width:auto で内容幅)。
            // 自己参照誤省略を避けるため CustomTruncateText には親 Stack の ref を渡し、
            // 親の内側幅 - chip 幅 で省略判定する。
            sx={{ width: "auto" }}
            containerRef={titleStackRef}
            // chip(~90) + 戻るボタン(icon24+gap8=32) 分を内側幅から引く。
            containerOffset={(areaTypeLabel ? 90 : 0) + (!embedded ? 32 : 0)}
          />
          {/* 場所区分チップ: 専有部 / 共用部 / 建物設備 / 敷地 / その他 */}
          {areaTypeLabel && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 700,
                lineHeight: 1.4,
                backgroundColor: "rgba(11,157,189,0.1)",
                color: "#0B9DBD",
                border: "1px solid #0B9DBD",
              }}
            >
              {areaTypeLabel}
            </Box>
          )}
        </Stack>
        {/* 物件名（タイトル直下）。
            CustomSectionTitle は左に縦線 (border-left 6px/7px) + pl:1.25 (10px)
            を持つため、物件名の文字頭をタイトルテキストの頭に揃えるには
            その分だけ pl をずらす。 */}
        {propertyName && (
          <Typography
            sx={{
              fontSize: { xs: "12px", md: "13px" },
              color: "#6b7280",
              mt: 0.25,
              fontWeight: 500,
              // 戻るボタン表示時はタイトルが icon(24)+gap(8)=32px から始まるので合わせる。
              pl: !embedded ? "32px" : { xs: "16px", sm: "17px" },
            }}
          >
            {propertyName}
          </Typography>
        )}
      </Box>

      {/* タブ（名称直下） */}
      <Box sx={{ backgroundColor: "white" }}>
        <Tabs
          value={activeTabIndex}
          onChange={(_event, newValue) => onTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: "30px",
            borderBottom: `1px solid ${colors.border.tab}`,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              position: "relative",
              minHeight: "30px",
              paddingTop: 0.5,
              paddingBottom: 0.5,
              paddingLeft: 2,
              paddingRight: 2,
              minWidth: "90px",
              textTransform: "none",
              fontFamily: '"Noto Sans JP"',
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "1.7",
              color: colors.text.secondary,
              "&:hover": {
                color: colors.primary.main,
                opacity: 1,
              },
              "&.Mui-selected": {
                color: colors.primary.main,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  backgroundColor: colors.primary.main,
                  borderRadius: "10px 10px 0 0",
                },
              },
            },
            "& .MuiTabs-scrollButtons": {
              color: colors.primary.main,
              "&.Mui-disabled": { opacity: 0.3 },
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};

export default DetailPageHeader;
